import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import DocumentCard from '../components/DocumentCard';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Lock, AlertCircle } from 'lucide-react';
import AddChargeModal from '../components/AddChargeModal';

const PAYMENT_STATUS = {
  UNDER_REVIEW: 'under_review',
  RESOLVED: 'resolved',
  REJECTED: 'rejected'
};

const PaymentReviewPage = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState(null);
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!paymentId) {
        setError({ type: 'error', message: 'Invalid payment ID' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/admin/billings/${paymentId}`);
        console.log('Payment details response:', response.data);
        console.log('Financial details:', response.data.financial_details);
        setPayment(response.data);
      } catch (err) {
        // Enhanced error handling
        if (err.response?.status === 404) {
          setError({
            type: 'not_found',
            message: err.response.data?.message || 'Payment not found'
          });
        } else if (err.response?.status === 401) {
          setError({
            type: 'unauthenticated',
            message: 'Please log in to view this payment'
          });
        } else if (err.response?.status === 403) {
          setError({
            type: 'unauthorized',
            message: 'You do not have permission to view this payment'
          });
        } else {
          setError({
            type: 'error',
            message: err.response?.data?.message || 'Failed to fetch payment details'
          });
        }
        console.error('Error fetching payment details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [paymentId]);

  const handleAction = async (action) => {
    if (!resolution.trim()) {
      toast.error('Please provide a resolution note', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }

    try {
      await api.post(`/api/billings/${paymentId}/action`, {
        action,
        resolution,
        comments: resolution
      });
      
      // Show success message based on action type
      const actionMessage = action === 'resolve_dispute' ? 'resolved' : 'rejected';
      toast.success(`Payment dispute ${actionMessage} successfully`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      // Refresh the payment details instead of navigating away
      const response = await api.get(`/api/admin/billings/${paymentId}`);
      setPayment(response.data);
      // Clear the resolution text area
      setResolution('');

    } catch (error) {
      console.error('Error handling dispute action:', error);
      toast.error(error.response?.data?.error || 'Error processing action. Please try again.', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const handleChargeAdded = async (newCharge) => {
    try {
      const response = await api.get(`/api/admin/billings/${paymentId}`);
      setPayment(response.data);
    } catch (error) {
      console.error('Error refreshing payment details:', error);
      toast.error('Failed to refresh payment details');
    }
  };

  // Add helper function to check if payment has any adjustments
  const hasAnyAdjustments = () => {
    if (!payment?.financial_details?.adjustments_summary) return false;
    const summary = payment.financial_details.adjustments_summary;
    return (
      summary.approved > 0 || 
      summary.pending > 0 || 
      summary.rejected > 0 || 
      summary.disputed > 0
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          {error.type === 'unauthorized' ? (
            <>
              <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-6">
                You don't have permission to view this payment.
              </p>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Return to Dashboard
              </button>
            </>
          ) : error.type === 'unauthenticated' ? (
            <>
              <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
              <p className="text-gray-600 mb-6">
                Please log in to view this payment.
              </p>
              <div className="space-x-4">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Return Home
                </button>
              </div>
            </>
          ) : error.type === 'not_found' ? (
            <>
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Not Found</h2>
              <p className="text-gray-600 mb-6">
                {error.message || 'The requested payment could not be found.'}
              </p>
              <button
                onClick={() => navigate('/admin/dashboard?tab=disputes')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                View All Disputes
              </button>
            </>
          ) : (
            <>
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
              <p className="text-gray-600 mb-6">{error.message}</p>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Return to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Not Found</h2>
          <p className="text-gray-600 mb-6">
            The requested payment could not be found.
          </p>
          <button
            onClick={() => navigate('/admin/dashboard?tab=disputes')}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            View All Disputes
          </button>
        </div>
      </div>
    );
  }

  const { financial_details, carrier_details, customer_details, dispute_details, load_details } = payment;

  if (!user?.role === 'system_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            Only system administrators can access this page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  console.log('Current payment state:', payment);
  console.log('Financial details from state:', payment?.financial_details);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-100 p-3 pt-20 sm:p-6 sm:pt-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Review Payment Dispute
              </h1>
              <p className="text-gray-600">
                Load #{payment.load_id} - ${financial_details.final_amount.toFixed(2)}
              </p>
            </div>

            {/* Load Details */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Load Details</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600">Origin: {load_details.origin}</p>
                <p className="text-gray-600">Destination: {load_details.destination}</p>
                <p className="text-gray-600">Pickup: {new Date(load_details.pickup_date).toLocaleDateString()}</p>
                <p className="text-gray-600">Delivery: {new Date(load_details.delivery_date).toLocaleDateString()}</p>
                <p className="text-gray-600">Status: {load_details.status}</p>
              </div>
            </div>

            {/* Financial Details */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold">Financial Details</h2>
                <button
                  onClick={() => setIsAddChargeModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Charge
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Base Rate:</span>
                  <span className="font-medium">${financial_details.base_rate.toFixed(2)}</span>
                </div>

                {financial_details.adjustments_summary && (
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Approved Adjustments:</span>
                      <span className="text-green-600">${financial_details.adjustments_summary.approved.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Pending Adjustments:</span>
                      <span className="text-yellow-600">${financial_details.adjustments_summary.pending.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Rejected Adjustments:</span>
                      <span className="text-red-600">${financial_details.adjustments_summary.rejected.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {financial_details.adjustments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-medium mb-3">Adjustment Details</h3>
                    <div className="space-y-3">
                      {financial_details.adjustments.map((adj, index) => (
                        <div key={index} className="bg-white p-3 rounded border border-gray-200">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">
                                {adj.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </p>
                              <p className="text-sm text-gray-600">{adj.description}</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-medium ${
                                adj.status === 'approved' ? 'text-green-600' : 
                                adj.status === 'rejected' ? 'text-red-600' : 
                                'text-yellow-600'
                              }`}>
                                ${adj.amount.toFixed(2)}
                              </p>
                              <span className={`text-xs px-2 py-1 rounded ${
                                adj.status === 'approved' ? 'bg-green-100 text-green-800' :
                                adj.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {adj.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Final Amount:</span>
                    <span className="text-lg font-semibold text-gray-900">
                      ${financial_details.final_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h2 className="text-lg font-semibold mb-2">Carrier Details</h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-600">{carrier_details.name}</p>
                  <p className="text-gray-600">{carrier_details.admin_email}</p>
                  <p className="text-gray-600">MC#: {carrier_details.mc_number}</p>
                  <p className="text-gray-600">DOT#: {carrier_details.dot_number}</p>
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-2">Customer Details</h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-600">{customer_details.name}</p>
                  <p className="text-gray-600">{customer_details.admin_email}</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Dispute Information</h2>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="mb-4">
                  <p className="font-medium text-gray-900 mb-2">Dispute Reason:</p>
                  <p className="text-gray-800 bg-white p-3 rounded border border-yellow-200">
                    {dispute_details.dispute_reason || 'No reason provided'}
                  </p>
                </div>

                {dispute_details.dispute_comments && (
                  <div className="mb-4">
                    <p className="font-medium text-gray-900 mb-2">Dispute Comments:</p>
                    <p className="text-gray-800 bg-white p-3 rounded border border-yellow-200">
                      {dispute_details.dispute_comments}
                    </p>
                  </div>
                )}
                
                {dispute_details.dispute_resolution && (
                  <div className="mt-4 pt-4 border-t border-yellow-200">
                    <p className="font-medium text-gray-900 mb-2">Previous Resolution:</p>
                    <p className="text-gray-800 bg-white p-3 rounded border border-yellow-200">
                      {dispute_details.dispute_resolution}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      Resolved on: {new Date(dispute_details.resolution_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {payment.documents && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Load Documents</h2>
                
                {payment.documents.billing_documents.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">Billing Documents</h3>
                    <div className="space-y-2">
                      {payment.documents.billing_documents.map(doc => (
                        <DocumentCard key={doc.id} document={doc} />
                      ))}
                    </div>
                  </div>
                )}

                {payment.documents.dispute_documents.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">Dispute Documents</h3>
                    <div className="space-y-2">
                      {payment.documents.dispute_documents.map(doc => (
                        <DocumentCard key={doc.id} document={doc} />
                      ))}
                    </div>
                  </div>
                )}

                {payment.documents.load_documents.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">Load Documents</h3>
                    <div className="space-y-2">
                      {payment.documents.load_documents.map(doc => (
                        <DocumentCard key={doc.id} document={doc} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Add Status History Section before the Resolution Note */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Status History</h2>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                {payment.status_history.map((history, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg ${
                      index === 0 ? 'bg-white border border-gray-200' : 
                      history.status === 'adjustment_added' ? 'bg-blue-50 border-l-4 border-blue-200' :
                      'border-l-4 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className={`font-medium ${
                          history.status === 'adjustment_added' ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {history.status === 'adjustment_added' ? 'Rate Adjustment Added' : `Status: ${history.status}`}
                        </span>
                        <p className="text-sm text-gray-500">
                          By: {history.user.name} ({history.user.role})
                        </p>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(history.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {history.comments && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">
                          {history.comments}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Resolution Note and buttons remain the same */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Resolution Note</h2>
              <textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="w-full h-32 p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter your resolution details..."
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => handleAction('resolve_dispute')}
                disabled={payment.status !== PAYMENT_STATUS.UNDER_REVIEW}
                className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500
                  ${payment.status === PAYMENT_STATUS.UNDER_REVIEW
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-green-200 text-gray-500 cursor-not-allowed'
                  }`}
              >
                Resolve Dispute
              </button>
              <button
                onClick={() => handleAction('reject_dispute')}
                disabled={payment.status !== PAYMENT_STATUS.UNDER_REVIEW || hasAnyAdjustments()}
                className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500
                  ${payment.status === PAYMENT_STATUS.UNDER_REVIEW && !hasAnyAdjustments()
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-200 text-gray-500 cursor-not-allowed'
                  }`}
                title={hasAnyAdjustments() ? "Cannot reject dispute when rate adjustments exist" : ""}
              >
                Reject Dispute
              </button>
              <button
                onClick={() => navigate('/admin/dashboard?tab=payments')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Back to Payments
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <AddChargeModal
        isOpen={isAddChargeModalOpen}
        onClose={() => setIsAddChargeModalOpen(false)}
        paymentId={paymentId}
        onChargeAdded={handleChargeAdded}
      />
    </>
  );
};

export default PaymentReviewPage; 